import { shallowEqual, useSelector } from 'react-redux'
import { Identifier, Pagination, Record, RecordMap, ReduxState, Sort } from '../types'
import { recordsToRecordMap } from './recordsToRecordMap'
import useQueryWithStore, { QueryOptions } from './useQueryWithStore'

const defaultData = {}
const defaultRecords: any[] = []

interface FilterType {
  auto_string?: string
  [key: string]: any
}

// Registry of filter keys that should trigger POST requests (to avoid URL length limits)
const POST_FILTER_KEYS = ['auto_string'] as const

export const shouldUsePostForFilters = (filter: FilterType): boolean => {
  return POST_FILTER_KEYS.some((key) => filter[key])
}

const separatePostFilters = (filter: FilterType): { queryFilter: FilterType; postData: Partial<FilterType> } => {
  const remainingFilters = { ...filter }
  const postData: Partial<FilterType> = {}

  POST_FILTER_KEYS.forEach((key) => {
    if (filter.hasOwnProperty(key)) {
      postData[key] = filter[key]
      delete remainingFilters[key]
    }
  })

  return {
    queryFilter: remainingFilters,
    postData,
  }
}

/**
 * Specialized hook for handling list queries that require POST requests due to large filter parameters.
 *
 * This hook follows the same pattern as useGetListHandler but uses CUSTOM_POST queries to handle
 * filters that are too large for URL parameters. It maintains full Redux caching compatibility.
 *
 * IMPORTANT GOTCHAS:
 * - When running in post mode, the data list endpoints will automatically use the create path
 * - You will need to ensure that the create method is overridden for the viewset

 */
export const useGetListWithPost = <RecordType extends Record = Record>(
  resource: string,
  pagination: Pagination,
  sort: Sort,
  filter: FilterType,
  options?: QueryOptions
): {
  data?: RecordMap<RecordType>
  ids?: Identifier[]
  total?: number
  error?: any
  loading: boolean
  loaded: boolean
} => {
  const { queryFilter, postData } = separatePostFilters(filter)

  const payload = {
    url: `${resource}/?${new URLSearchParams({
      page: pagination.page.toString(),
      limit: pagination.perPage.toString(),
      ordering: sort.order === 'ASC' ? sort.field : `-${sort.field}`,
      ...(queryFilter && Object.fromEntries(Object.entries(queryFilter).map(([k, v]) => [k, String(v)]))),
    }).toString()}`,
    data: postData,
  }

  const recordsSelector = (state: ReduxState) => {
    const queryKey = JSON.stringify({
      type: 'CUSTOM_POST',
      resource,
      payload,
    })
    const queryData = state.admin.customQueries[queryKey]
    return queryData?.data || defaultRecords
  }

  const { data: records, total, error, loading, loaded } = useQueryWithStore(
    { type: 'CUSTOM_POST', resource, payload },
    { ...options, action: 'CUSTOM_QUERY' },
    recordsSelector,
    undefined
  )

  const data = useSelector((state: ReduxState): RecordMap<RecordType> => {
    if (!records || !Array.isArray(records)) return defaultData
    return recordsToRecordMap<RecordType>(records)
  }, shallowEqual)

  const ids =
    records && Array.isArray(records)
      ? records.map((item) => item.id).filter((id) => id !== undefined && id !== null)
      : defaultRecords
  const fallbackTotal = records && Array.isArray(records) ? records.length : 0

  return {
    data,
    ids,
    total: total !== undefined ? total : fallbackTotal,
    error,
    loading,
    loaded,
  }
}

export default useGetListWithPost
